import React, { useState } from 'react';
import { User, Settings, Bell, LogOut, Upload, Building2, UserPlus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ProfileSettings } from './ProfileSettings';
import { CompanyAdminPanel } from './CompanyAdminPanel';
import { NotificationPreferences } from './NotificationPreferences';
import { InviteClientUserDialog } from './InviteClientUserDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

interface UserAvatarMenuProps {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl?: string;
  };
  companyId: string;
  companyName?: string;
  accessToken?: string;
  projectId: string;
  publicAnonKey: string;
  onLogout: () => void;
}

export function UserAvatarMenu({ user, companyId, companyName, accessToken, projectId, publicAnonKey, onLogout }: UserAvatarMenuProps) {
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [showNotificationPreferences, setShowNotificationPreferences] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary">
            <Avatar className="h-9 w-9 cursor-pointer">
              <AvatarImage src={user.avatarUrl} alt={user.name} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="font-medium leading-none">{user.name}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
              <p className="text-xs leading-none text-muted-foreground mt-1 capitalize">
                {user.role.replace('_', ' ')}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Company Settings - Only for Company Admins */}
          {user.role === 'companyAdmin' && (
            <>
              <DropdownMenuItem onClick={() => setShowCompanySettings(true)}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>Company Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowInvite(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                <span>Invite teammate</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          <DropdownMenuItem onClick={() => setShowProfileSettings(true)}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Profile Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowNotificationPreferences(true)}>
            <Bell className="mr-2 h-4 w-4" />
            <span>Notification Preferences</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={(e) => {
              console.log('[LOGOUT_TRACE] UserAvatarMenu - Logout clicked');
              console.log('[LOGOUT_TRACE] onLogout exists:', typeof onLogout === 'function');
              console.log('[LOGOUT_TRACE] onLogout value:', onLogout);
              console.log('[LOGOUT_TRACE] Invoking onLogout...');
              e.preventDefault();
              try {
                onLogout();
                console.log('[LOGOUT_TRACE] onLogout invoked successfully');
              } catch (error) {
                console.error('[LOGOUT_TRACE] ❌ Error invoking onLogout:', error);
              }
            }}
            className="text-red-600"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showProfileSettings && (
        <ProfileSettings
          user={user}
          companyId={companyId}
          projectId={projectId}
          publicAnonKey={publicAnonKey}
          accessToken={accessToken}
          onClose={() => setShowProfileSettings(false)}
        />
      )}

      {/* The client lead's settings (step 3 of the simplification plan): the company's people,
          their permissions, and the company switches (AI, notifications, the AI call log).
          The quick settings dialog that sat here is built on consultant routes (template,
          status, the firm's company list) that a client cannot call. */}
      {showCompanySettings && user.role === 'companyAdmin' && (
        <Dialog open={showCompanySettings} onOpenChange={setShowCompanySettings}>
          <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Company settings</DialogTitle>
            </DialogHeader>
            <CompanyAdminPanel
              accessToken={accessToken || publicAnonKey}
              companyId={companyId}
              companyName={companyName || user.name}
              currentUser={user}
            />
          </DialogContent>
        </Dialog>
      )}

      {showInvite && user.role === 'companyAdmin' && accessToken && (
        <InviteClientUserDialog
          open={showInvite}
          onOpenChange={setShowInvite}
          companyId={companyId}
          companyName={companyName}
          accessToken={accessToken}
        />
      )}

      {showNotificationPreferences && (
        <Dialog open={showNotificationPreferences} onOpenChange={setShowNotificationPreferences}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Notification Preferences</DialogTitle>
            </DialogHeader>
            <NotificationPreferences
              currentUser={user}
              accessToken={accessToken || publicAnonKey}
              onClose={() => setShowNotificationPreferences(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}